import { createLogger } from "@/lib/logger";
import { CodeRuntime } from "./client";
import {
  type Artifact,
  type LearnerState,
  type UINode,
  currentPage,
  initialState,
  pageProgram,
  visibleText,
} from "./contract";
import { checkAnswer, migrateState } from "./state";

const logger = createLogger("GeneratedExercise");
export type CodeEvent = {
  type: string;
  session: string;
  id: string;
  revision: number;
  sequence: number;
  [key: string]: unknown;
};
export type Snapshot = {
  artifact: Artifact;
  state: LearnerState;
  tree: UINode | null;
  fallback: boolean;
  fault: string | null;
  hidden?: boolean;
};
export const controllers = new Map<string, ExerciseController>();
export const controllerKey = (a: { session: string; id: string }) => `${a.session}:${a.id}`;
export class ExerciseController {
  private listeners = new Set<() => void>();
  private runtime: CodeRuntime | null = null;
  private queue: Promise<void> = Promise.resolve();
  private closed = false;
  private epoch = 0;
  private displayed = 0;
  private displayedPage = "";
  private mountedVisible = false;
  private navigation = 0;
  private layoutFallbacks = new Set<string>();
  private navigatedAt: number | null = null;
  private editing = new Set<string>();
  private idleWaiters = new Set<() => void>();
  snapshot: Snapshot;
  constructor(
    artifact: Artifact,
    private emit: (event: CodeEvent) => void
  ) {
    this.snapshot = { artifact, state: initialState(), tree: null, fallback: false, fault: null };
  }
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  getSnapshot = () => this.snapshot;
  private changed() {
    this.snapshot = { ...this.snapshot };
    this.listeners.forEach((fn) => fn());
  }
  editingField(id: string, active: boolean) {
    if (active) this.editing.add(id);
    else this.editing.delete(id);
    if (!this.editing.size) {
      this.idleWaiters.forEach((fn) => fn());
      this.idleWaiters.clear();
    }
  }
  private idle() {
    if (!this.editing.size) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const finish = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        this.idleWaiters.delete(finish);
        reject(new Error("Update deferred while learner is editing"));
      }, 9000);
      this.idleWaiters.add(finish);
    });
  }
  private event(type: string, extra: Record<string, unknown> = {}) {
    const a = this.snapshot.artifact;
    this.emit({
      type,
      session: a.session,
      id: a.id,
      revision: a.revision,
      sequence: ++this.snapshot.state.sequence,
      ...this.pageFacts(),
      ...extra,
    });
  }
  pageFacts() {
    const { program } = this.snapshot.artifact;
    const page = currentPage(program, this.snapshot.state);
    return page
      ? {
          page: page.id,
          pageIndex: program.pages!.indexOf(page),
          visibleActivities: page.activities,
        }
      : {};
  }
  isFallback() {
    return (
      this.snapshot.fallback ||
      this.layoutFallbacks.has(
        currentPage(this.snapshot.artifact.program, this.snapshot.state)?.id ?? ""
      )
    );
  }
  overflow() {
    const page = currentPage(this.snapshot.artifact.program, this.snapshot.state);
    if (!page || this.isFallback() || this.closed) return;
    this.layoutFallbacks.add(page.id);
    this.changed();
    logger.info("Page using compact layout", { id: this.snapshot.artifact.id, page: page.id });
    this.event("fault", { reason: "Page layout overflow" });
    if (this.mountedVisible) this.mounted(true);
  }
  private visibleActivity(id: string) {
    return pageProgram(this.snapshot.artifact.program, this.snapshot.state).activities.find(
      (a) => a.id === id
    );
  }
  pool(id: string) {
    const state = this.snapshot.state;
    if (state.pools?.[id]) return state.pools[id];
    const indices = this.visibleActivity(id)?.options.map((_, i) => i) ?? [];
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    state.pools = { ...state.pools, [id]: indices };
    return indices;
  }
  navigate(offset: number) {
    if (this.closed || ![-1, 1].includes(offset)) return;
    const { program } = this.snapshot.artifact;
    const page = currentPage(program, this.snapshot.state);
    if (!page) return;
    const next = program.pages![program.pages!.indexOf(page) + offset];
    if (!next) return;
    this.navigation++;
    this.navigatedAt = performance.now();
    this.editing.clear();
    this.editingField("", false);
    this.snapshot = {
      ...this.snapshot,
      tree: null,
      state: { ...this.snapshot.state, viewedPageId: next.id },
    };
    this.changed(); // Host controls for the new page are available immediately.
    this.action("page_render");
  }
  visibility(visible: boolean, reason?: string) {
    if (this.closed || this.mountedVisible === visible) return;
    this.mountedVisible = visible;
    if (visible) this.mounted(true);
    else this.event("hidden", reason ? { reason } : {});
  }
  returnToConversation() {
    this.visibility(false, "return_to_conversation");
    this.snapshot = { ...this.snapshot, hidden: true };
    this.changed();
  }
  reopen() {
    this.snapshot = { ...this.snapshot, hidden: false };
    this.changed();
  }
  resume() {
    this.event("resume", {
      open: this.mountedVisible && !this.snapshot.hidden,
      visible: this.semantic(),
    });
  }
  async load(artifact: Artifact) {
    const epoch = ++this.epoch;
    const candidate = new CodeRuntime();
    const start = performance.now();
    try {
      const state =
        artifact.revision === this.snapshot.artifact.revision
          ? this.snapshot.state
          : migrateState(this.snapshot.artifact.program, artifact.program, this.snapshot.state);
      let output = await candidate.call(artifact.program, state, true);
      if (this.closed || epoch !== this.epoch) {
        candidate.close();
        return false;
      }
      await this.idle();
      // Input may have changed while the compiler warmed. Reconcile from the latest checkpoint.
      let latest = state;
      for (let attempt = 0; attempt < 3; attempt++) {
        const checkpoint = this.snapshot.state;
        latest = migrateState(this.snapshot.artifact.program, artifact.program, checkpoint);
        output = await candidate.call(artifact.program, latest, false);
        if (checkpoint === this.snapshot.state) break;
        if (attempt === 2) throw new Error("Update deferred while learner is navigating");
      }
      if (this.closed || epoch !== this.epoch) {
        candidate.close();
        return false;
      }
      this.runtime?.close();
      this.runtime = candidate;
      this.layoutFallbacks.clear();
      this.snapshot = {
        artifact,
        state: {
          ...latest,
          view: output.view,
        },
        tree: output.tree,
        fallback: false,
        fault: null,
        hidden: this.snapshot.hidden,
      };
      this.changed();
      logger.info("Executable exercise ready", {
        id: artifact.id,
        revision: artifact.revision,
        elapsedMs: Math.round(performance.now() - start),
      });
      return true;
    } catch (error) {
      candidate.close();
      if (this.closed || epoch !== this.epoch) return false;
      if (this.snapshot.tree || this.displayed > 0) {
        this.emit({
          type: "fault",
          session: artifact.session,
          id: artifact.id,
          revision: artifact.revision,
          sequence: ++this.snapshot.state.sequence,
          reason: String(error).slice(0, 300),
        });
        return false;
      }
      this.snapshot = {
        ...this.snapshot,
        artifact,
        fallback: true,
        fault: String(error).slice(0, 300),
      };
      this.event("fault", { reason: this.snapshot.fault });
      this.changed();
      return true;
    }
  }
  mounted(force = false) {
    if (this.closed) return;
    const page = currentPage(this.snapshot.artifact.program, this.snapshot.state)?.id ?? "";
    if (!force && this.displayed === this.snapshot.artifact.revision && this.displayedPage === page)
      return;
    const type =
      this.displayed === this.snapshot.artifact.revision && this.displayedPage !== page
        ? "page"
        : "displayed";
    this.displayed = this.snapshot.artifact.revision;
    this.displayedPage = page;
    this.mountedVisible = true;
    const navigationMs =
      this.navigatedAt === null ? undefined : Math.round(performance.now() - this.navigatedAt);
    this.navigatedAt = null;
    this.event(type, {
      fallback: this.isFallback(),
      visible: this.semantic(),
      ...(navigationMs === undefined ? {} : { navigationMs }),
    });
    if (navigationMs !== undefined) logger.info("Exercise page mounted", { page, navigationMs });
  }
  semantic() {
    return this.snapshot.tree && !this.isFallback()
      ? visibleText(this.snapshot.tree, this.snapshot.artifact.program, this.snapshot.state)
      : [
          pageProgram(this.snapshot.artifact.program, this.snapshot.state).passage,
          ...pageProgram(this.snapshot.artifact.program, this.snapshot.state).activities.map(
            (a) => a.prompt
          ),
        ]
          .join(" ")
          .slice(0, 4000);
  }
  answer(id: string, value: string) {
    if (this.closed || value.length > 2000 || this.snapshot.state.results[id] === "correct") return;
    if (!this.visibleActivity(id)) return;
    this.snapshot.state = {
      ...this.snapshot.state,
      answers: { ...this.snapshot.state.answers, [id]: value },
    };
    this.changed();
  }
  check(id: string) {
    const a = this.visibleActivity(id);
    if (
      this.closed ||
      !a ||
      !this.snapshot.state.answers[id]?.trim() ||
      this.snapshot.state.results[id] === "correct"
    )
      return;
    const state = this.snapshot.state;
    const result = checkAnswer(a, state.answers[id]);
    this.editingField(id, false);
    this.snapshot.state = {
      ...state,
      attempts: { ...state.attempts, [id]: (state.attempts[id] || 0) + 1 },
      results: { ...state.results, [id]: result },
    };
    this.event("check", {
      activity: id,
      answer: state.answers[id],
      result,
      attempt: this.snapshot.state.attempts[id],
      resolved: this.snapshot.artifact.program.activities.every(
        (a) => this.snapshot.state.results[a.id] === "correct"
      ),
    });
    this.changed();
    this.action("checked", id);
  }
  hint(id: string) {
    if (this.closed || !this.visibleActivity(id)) return;
    this.snapshot.state = {
      ...this.snapshot.state,
      hints: { ...this.snapshot.state.hints, [id]: true },
    };
    this.event("help", { activity: id });
    this.changed();
  }
  order(id: string, order: number[]) {
    const a = this.visibleActivity(id);
    if (
      !a ||
      a.mode !== "sequence" ||
      this.snapshot.state.results[id] === "correct" ||
      order.length > a.options.length ||
      new Set(order).size !== order.length ||
      order.some((i) => !Number.isInteger(i) || i < 0 || i >= a.options.length)
    )
      return;
    this.snapshot.state = {
      ...this.snapshot.state,
      orders: { ...this.snapshot.state.orders, [id]: order },
    };
    this.answer(id, order.map((i) => a.options[i]).join(" "));
  }
  action(id: string, value?: string | number | boolean) {
    if (this.closed || !/^[\w.-]{1,64}$/.test(id)) return;
    const navigation = this.navigation;
    this.queue = this.queue.then(async () => {
      if (!this.runtime || this.closed || this.isFallback() || navigation !== this.navigation)
        return;
      const epoch = this.epoch;
      try {
        const output = await this.runtime.call(
          this.snapshot.artifact.program,
          this.snapshot.state,
          false,
          id === "page_render" ? undefined : { id, value }
        );
        if (this.closed || epoch !== this.epoch || navigation !== this.navigation) return;
        const before = this.semantic();
        this.snapshot = {
          ...this.snapshot,
          tree: output.tree,
          state: { ...this.snapshot.state, view: output.view },
        };
        this.changed();
        if (before !== this.semantic()) this.event("view", { visible: this.semantic() });
      } catch (error) {
        if (epoch === this.epoch && navigation === this.navigation) this.fallback(String(error));
      }
    });
  }
  cancelPending() {
    this.epoch++;
    this.idleWaiters.forEach((fn) => fn());
    this.idleWaiters.clear();
  }
  fallback(reason = "Code mode disabled", reportFault = true) {
    this.epoch++;
    this.runtime?.close();
    this.runtime = null;
    this.snapshot = { ...this.snapshot, fallback: true, fault: reason.slice(0, 300) };
    this.changed();
    if (reportFault) this.event("fault", { reason: this.snapshot.fault });
    if (this.mountedVisible) this.mounted(true);
  }
  dismiss() {
    this.event("closed");
    this.close();
  }
  close() {
    this.closed = true;
    this.cancelPending();
    this.runtime?.close();
    this.runtime = null;
    this.listeners.clear();
  }
}
